import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api } from '@/api/client';
import { useQueryClient } from '@tanstack/react-query';
import { DocumentosTab } from '@/components/DocumentosTab';
import { DjhvWizard } from '@/components/DjhvWizard';
import { AsignarDesignadoForm } from '@/components/AsignarDesignadoForm';
import { MiListaTab } from '@/components/MiListaTab';
import { ListaMiembrosTab } from '@/components/ListaMiembrosTab';
import { ContrasteDniBox } from '@/components/ContrasteDniBox';
import { FirmaTab } from '@/components/FirmaTab';
import { useAuth } from '@/store/auth';
import { labelNivel } from '@/lib/labels';

interface Cand {
  id: number;
  id_onpe: string;
  dni: string | null;
  nombres_onpe: string | null;
  apellidos_onpe: string | null;
  region: string;
  provincia: string | null;
  distrito: string | null;
  nivel: string;
  tipo_eleccion: string;
  n_lista: number | null;
  orden: number | null;
  cargo: string;
  es_designado: boolean;
  sexo: string | null;
  fecha_nacimiento: string | null;
  edad_al_cierre: number | null;
  ubigeo_reniec: string | null;
  en_padron_arpp: boolean;
  en_padron_postulable: boolean;
  op_canonica: string | null;
  desafiliado_jne: boolean;
  expediente_desafiliacion: string | null;
  ubigeo_circunscripcion: string | null;
  mismatch_territorial: boolean;
  severidad_mismatch: string | null;
  requiere_domicilio_multiple: boolean;
  domicilio_multiple_acreditado: boolean;
  estado_djhv: string;
  estado_documentos: string;
  estado_validacion: string;
  estado_expediente_jee: string;
  estado_supervision?: string;
  subido_declara?: boolean;
  subido_declara_at?: string | null;
  n_expediente_declara?: string | null;
}

const TABS_STAFF = ['Datos', 'Documentos', 'DJHV', 'Lista', 'Validación'] as const;
const TABS_CAN = ['Datos', 'Documentos', 'DJHV', 'Mi lista', 'Firma'] as const;
type Tab = typeof TABS_STAFF[number] | typeof TABS_CAN[number];

const SEV_PILL: Record<string, string> = {
  ok: 'bg-green-100 text-green-700',
  observacion: 'bg-amber-100 text-amber-700',
  bloqueante: 'bg-red-100 text-red-700',
};

const SEV_LABEL: Record<string, string> = {
  BAJA: 'bg-blue-100 text-blue-700',
  MEDIA: 'bg-amber-100 text-amber-700',
  ALTA: 'bg-red-100 text-red-700',
};

const SUP_LABEL: Record<string, { txt: string; cls: string }> = {
  borrador_can:  { txt: 'Borrador (candidato)',  cls: 'bg-gray-100 text-gray-700' },
  enviado_asi:   { txt: 'Enviado a ASI',         cls: 'bg-blue-100 text-blue-800' },
  observado_asi: { txt: 'Observado por ASI',     cls: 'bg-amber-100 text-amber-800' },
  aprobado_asi:  { txt: 'Aprobado por ASI',      cls: 'bg-emerald-100 text-emerald-800' },
  listo_declara: { txt: 'Listo para DECLARA',    cls: 'bg-violet-100 text-violet-800' },
};

function PlantillaBotones({ candidatoId }: { candidatoId: number }) {
  const descargar = async (formato: 'docx' | 'xlsx') => {
    const r = await api.get(`/plantillas/candidato/${candidatoId}/blank.${formato}`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `candidato_${candidatoId}_FUDJHV.${formato}`;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  return (
    <div className="flex flex-col gap-1 mt-2 items-end">
      <button
        onClick={() => descargar('docx')}
        className="text-[10px] bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded inline-flex items-center gap-1"
        title="FUDJHV en Word (para firmar a mano)"
      >
        📄 FUDJHV (Word)
      </button>
      <button
        onClick={() => descargar('xlsx')}
        className="text-[10px] bg-emerald-700 hover:bg-emerald-800 text-white px-2 py-1 rounded inline-flex items-center gap-1"
        title="Plantilla en Excel para captura"
      >
        📊 Plantilla (Excel)
      </button>
    </div>
  );
}


function DeclaraBoton({ candidato, onChange }: { candidato: Cand; onChange: () => void }) {
  const [editing, setEditing] = useState(false);
  const [nExp, setNExp] = useState(candidato.n_expediente_declara ?? '');

  const marcarMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/declara/candidatos/${candidato.id}/marcar-subido`, {
        n_expediente_declara: nExp.trim() || null,
      })).data,
    onSuccess: () => {
      setEditing(false);
      onChange();
    },
  });

  const desmarcarMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/declara/candidatos/${candidato.id}/desmarcar`, {})).data,
    onSuccess: () => onChange(),
  });

  if (candidato.subido_declara) {
    return (
      <button
        onClick={() => {
          if (confirm('¿Desmarcar la subida a DECLARA?')) desmarcarMut.mutate();
        }}
        disabled={desmarcarMut.isPending}
        className="text-[10px] px-2 py-0.5 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
      >
        {desmarcarMut.isPending ? '…' : 'Desmarcar DECLARA'}
      </button>
    );
  }

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="text-[11px] px-2 py-1 rounded bg-violet-600 hover:bg-violet-700 text-white"
      >
        ✓ Marcar subido DECLARA
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <input
        type="text"
        autoFocus
        placeholder="N° Exp DECLARA"
        value={nExp}
        onChange={(e) => setNExp(e.target.value)}
        className="text-[11px] border rounded px-2 py-1 w-32"
      />
      <button
        onClick={() => marcarMut.mutate()}
        disabled={marcarMut.isPending}
        className="text-[11px] px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
      >
        OK
      </button>
      <button
        onClick={() => setEditing(false)}
        className="text-[11px] px-2 py-1 rounded bg-gray-200 text-gray-700"
      >
        ✕
      </button>
    </div>
  );
}


function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex border-b py-1.5">
      <div className="text-xs text-gray-500 w-44 shrink-0">{label}</div>
      <div className="text-xs text-gray-900 flex-1">{value}</div>
    </div>
  );
}

export function FichaCandidato() {
  const { id } = useParams();
  const { rol } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('Datos');

  const { data: c, isLoading } = useQuery<Cand>({
    queryKey: ['cand', id],
    queryFn: async () => (await api.get<Cand>(`/candidatos/${id}`)).data,
    enabled: !!id,
  });

  const puedeAsignar = ['PLT', 'PLA', 'ADM', 'ASI'].includes(rol ?? '');
  const esCandidato = rol === 'CAN';
  const TABS: readonly Tab[] = esCandidato ? TABS_CAN : TABS_STAFF;

  if (isLoading || !c) return <div className="p-6 text-sm text-gray-500">Cargando…</div>;

  const nombre = c.es_designado && !c.nombres_onpe
    ? '[DESIGNADO PENDIENTE]'
    : [c.apellidos_onpe, c.nombres_onpe].filter(Boolean).join(', ');

  return (
    <div className="p-6 space-y-4 max-w-6xl">
      <div className="text-xs">
        <Link to="/candidatos" className="text-arpp-primary">← Candidatos</Link>
        {c.n_lista && (
          <> · <Link to={`/listas`} className="text-arpp-primary">Listas</Link></>
        )}
      </div>

      <header className="bg-white border rounded p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <h1 className="text-lg font-bold text-arpp-primary">{nombre}</h1>
            <p className="text-xs text-gray-600 mt-1">
              DNI <span className="font-mono">{c.dni ?? '—'}</span> · ONPE #{c.id_onpe}
            </p>
            {/* Cargo destacado */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="bg-arpp-primary text-white px-3 py-1.5 rounded text-sm font-bold uppercase tracking-wide">
                {c.cargo}
                {c.orden !== null && c.orden !== undefined && c.orden > 0 && <span className="ml-2 opacity-80">· pos {c.orden}</span>}
              </span>
              {c.cargo !== c.tipo_eleccion && (
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                  Lista de: {labelNivel(c.tipo_eleccion)}
                </span>
              )}
              {c.n_lista && (
                <span className="text-[10px] text-gray-500 italic" title="Lista interna de primarias ARPP — dato referencial. Para JEE existe una sola lista por circunscripción.">
                  primaria interna #{c.n_lista}
                </span>
              )}
              <span className="text-xs bg-gray-50 border border-gray-200 text-gray-700 px-2 py-1 rounded">
                {c.region}{c.provincia ? ` / ${c.provincia}` : ''}{c.distrito ? ` / ${c.distrito}` : ''}
              </span>
              {c.es_designado && !c.dni && (
                <span className="text-xs bg-amber-100 border border-amber-300 text-amber-800 px-2 py-1 rounded">
                  🟡 DESIGNADO PENDIENTE
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <span className={`text-[11px] px-2 py-1 rounded ${SEV_PILL[c.estado_validacion] || ''}`}>
              Validación: {c.estado_validacion}
            </span>
            <span className="text-[11px] px-2 py-1 rounded bg-gray-100 text-gray-700">
              Documentos: {c.estado_documentos}
            </span>
            <span className="text-[11px] px-2 py-1 rounded bg-gray-100 text-gray-700">
              DJHV: {c.estado_djhv}
            </span>
            <span className="text-[11px] px-2 py-1 rounded bg-gray-100 text-gray-700">
              JEE: {c.estado_expediente_jee}
            </span>
            {c.estado_supervision && (
              <span className={`text-[11px] px-2 py-1 rounded ${SUP_LABEL[c.estado_supervision]?.cls || 'bg-gray-100 text-gray-700'}`}>
                ⚙️ {SUP_LABEL[c.estado_supervision]?.txt || c.estado_supervision}
              </span>
            )}
            {c.subido_declara && (
              <span className="text-[11px] px-2 py-1 rounded bg-emerald-100 text-emerald-900 font-medium">
                ✅ Subido a DECLARA{c.n_expediente_declara ? ` · #${c.n_expediente_declara}` : ''}
              </span>
            )}
            {puedeAsignar && (
              <DeclaraBoton candidato={c} onChange={() => qc.invalidateQueries({ queryKey: ['cand', id] })} />
            )}
            {(rol === 'PLT' || rol === 'PLA' || rol === 'ADM' || rol === 'ASI' || rol === 'CAR_DJHV' || rol === 'CAN') && (
              <PlantillaBotones candidatoId={c.id} />
            )}
          </div>
        </div>
      </header>

      {c.es_designado && !c.dni && puedeAsignar && (
        <AsignarDesignadoForm
          candidatoId={c.id}
          onSuccess={() => qc.invalidateQueries({ queryKey: ['cand', id] })}
        />
      )}

      <nav className="flex gap-1 border-b">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px ${
              tab === t
                ? 'border-arpp-primary text-arpp-primary font-medium'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {t}
          </button>
        ))}
      </nav>

      {tab === 'Datos' && esCandidato && (
        <ContrasteDniBox />
      )}

      {tab === 'Datos' && (
        <section className="bg-white border rounded p-4 grid md:grid-cols-2 gap-x-8">
          <div>
            <h3 className="text-xs font-semibold uppercase text-gray-500 mb-2">Identidad</h3>
            <Row label="DNI" value={<span className="font-mono">{c.dni ?? '— (designado)'}</span>} />
            <Row label="Nombres ONPE" value={c.nombres_onpe ?? '—'} />
            <Row label="Apellidos ONPE" value={c.apellidos_onpe ?? '—'} />
            <Row label="Sexo" value={c.sexo ?? '—'} />
            <Row label="Fecha nacimiento" value={c.fecha_nacimiento ?? '—'} />
            <Row label="Edad al cierre" value={c.edad_al_cierre ?? '—'} />

            <h3 className="text-xs font-semibold uppercase text-gray-500 mt-4 mb-2">Padrón JNE</h3>
            <Row label="OP canónica" value={c.op_canonica ?? '—'} />
            <Row label="En padrón postulable (V09)" value={c.en_padron_postulable ? '✅ Sí' : '❌ No'} />
            <Row label="En padrón ARPP (primarias)" value={c.en_padron_arpp ? '✓' : '—'} />
            <Row label="Desafiliado JNE" value={c.desafiliado_jne ? <span className="text-red-700">⚠ Sí · Exp. {c.expediente_desafiliacion ?? 'N/D'}</span> : 'No'} />
          </div>
          <div>
            <h3 className="text-xs font-semibold uppercase text-gray-500 mb-2">Candidatura</h3>
            <Row label="Nivel" value={labelNivel(c.tipo_eleccion)} />
            <Row label="Cargo" value={c.cargo} />
            <Row label="Nivel" value={c.nivel} />
            <Row label="Región" value={c.region} />
            <Row label="Provincia" value={c.provincia ?? '—'} />
            <Row label="Distrito" value={c.distrito ?? '—'} />
            <Row label="Lista primaria interna (referencial)" value={c.n_lista ?? '—'} />
            <Row label="Orden" value={c.orden ?? '—'} />
            <Row label="Es designado" value={c.es_designado ? '🟡 SÍ — pendiente CEN' : 'No'} />

            <h3 className="text-xs font-semibold uppercase text-gray-500 mt-4 mb-2">Territorio</h3>
            <Row label="Ubigeo RENIEC" value={c.ubigeo_reniec ?? '—'} />
            <Row label="Ubigeo circunscripción" value={c.ubigeo_circunscripcion ?? '—'} />
            <Row label="Mismatch territorial" value={
              c.mismatch_territorial
                ? <span className={`text-[11px] px-2 py-0.5 rounded ${SEV_LABEL[c.severidad_mismatch ?? ''] || 'bg-amber-100 text-amber-700'}`}>⚠ {c.severidad_mismatch ?? 'sí'}</span>
                : '✓ OK'
            } />
            <Row label="Requiere dom. múltiple" value={c.requiere_domicilio_multiple ? '⚠ Sí' : 'No'} />
            <Row label="Dom. múltiple acreditado" value={c.domicilio_multiple_acreditado ? '✅' : '—'} />
          </div>
        </section>
      )}

      {tab === 'Documentos' && <DocumentosTab candidatoId={c.id} dni={c.dni} rol={rol ?? ''} />}

      {tab === 'DJHV' && (
        <DjhvWizard
          candidatoId={c.id}
          canEdit={['PLT', 'PLA', 'ADM', 'ASI', 'CAN'].includes(rol ?? '')}
        />
      )}

      {tab === 'Validación' && (
        <section className="bg-white border rounded p-4">
          <p className="text-xs text-gray-600 mb-3">
            Estado consolidado: <span className={`text-[11px] px-2 py-0.5 rounded ${SEV_PILL[c.estado_validacion]}`}>{c.estado_validacion}</span>
          </p>
          <p className="text-xs text-gray-500">
            Detalle por regla: ver módulo <Link to="/validacion" className="text-arpp-primary">Validación</Link> y correr motor.
            Cuando esté listo el drill-down por candidato lo mostraré aquí.
          </p>
        </section>
      )}

      {tab === 'Mi lista' && <MiListaTab />}
      {tab === 'Lista' && <ListaMiembrosTab candidatoId={c.id} />}
      {tab === 'Firma' && <FirmaTab />}
    </div>
  );
}
